import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useWords } from '@/shared/hooks/useWords.ts'
import useSWR from 'swr'
import { Plus, Workflow } from 'lucide-react'
import { Badge, Button, cn } from '@ludus/ui'
import type { PipelineId, PipelineKind } from '@ludus/core'
import { listFlows } from '@/lib/api/flows.ts'
import { fetchPipelineStatuses } from '@/lib/api/pipelines.ts'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'
import { FlowCard } from './FlowCard.tsx'
import { NewFlowDialog } from './NewFlowDialog.tsx'

interface FlowsSectionProps {
  pipelineId: PipelineId
  kind: PipelineKind
}

/**
 * The flows this pipeline fires, on its settings page beside `SourceSection` —
 * the mounting precedent, and the same shape: self-contained, its own SWR key,
 * and it never asks the page for data.
 *
 * The statuses read is keyed `/pipelines/:id/statuses`, which is the key the
 * settings page already holds, so it costs nothing here and gives the trigger
 * sentence a stage *name* instead of an id.
 */
export function FlowsSection({ pipelineId, kind }: FlowsSectionProps) {
  const words = useWords(kind)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)

  const key = `/pipelines/${pipelineId}/flows`
  const { data, error, isLoading, mutate } = useSWR(key, () => listFlows(pipelineId))
  const statuses = useSWR(`/pipelines/${pipelineId}/statuses`, () =>
    fetchPipelineStatuses(pipelineId),
  )

  const flows = data ?? []
  const statusNames = new Map((statuses.data ?? []).map((s) => [String(s.id), s.name]))

  /**
   * The catalogue re-read, as a background action neither the card nor the
   * dialog can fail on. Both call it *after* a write that has already landed,
   * so a rejected revalidation means a stale list, never a failed write — and
   * SWR reports it on `error`, which `summary()` already renders. Swallowed
   * here so it cannot travel back up as an unhandled rejection.
   */
  const revalidate = (): void => {
    void mutate().catch(() => undefined)
  }

  function summary(): string {
    if (isLoading)
      return t('pages.pipelines.settings.flows.loading', { defaultValue: 'Reading flows…' })
    if (error != null)
      return t('pages.pipelines.settings.flows.error', {
        defaultValue: 'The flows could not be read.',
      })
    const enabled = flows.filter((f) => f.enabled).length
    // `total`, not `count`: the locale rows spell `{{total}}`, and `count` is
    // i18next's plural selector — passing it would send this key through plural
    // resolution it has no `_one`/`_other` rows for while leaving `{{total}}`
    // unresolved, so the operator would read the braces.
    return t('pages.pipelines.settings.flows.summary', {
      total: flows.length,
      enabled,
      defaultValue: '{{enabled}} of {{total}} enabled.',
    })
  }

  return (
    <section className="@container flex flex-col gap-3 rounded-xl border border-border bg-background-secondary p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
            flows.length === 0 ? 'bg-background text-muted' : 'bg-primary-light text-primary',
          )}
        >
          <Workflow className="h-4 w-4" aria-hidden="true" />
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <h2 className="text-xs font-semibold text-foreground">
              {t('pages.pipelines.settings.flows.title', { defaultValue: 'Flows' })}
            </h2>
            {!isLoading && error == null && flows.length > 0 && (
              <Badge tone="muted" size="sm">
                {String(flows.length)}
              </Badge>
            )}
          </div>
          <p className="truncate text-xs text-muted">{summary()}</p>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isLoading}
          className="w-full shrink-0 @sm:w-auto"
          onClick={() => setDialogOpen(true)}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {t('pages.pipelines.settings.flows.new.button', { defaultValue: 'New flow' })}
        </Button>
      </div>

      {!isLoading && error == null && flows.length === 0 && (
        <CenteredPanel
          title={t('pages.pipelines.settings.flows.empty.title', { defaultValue: 'No flows' })}
          body={words('pages.pipelines.settings.flows.empty.body', {
            defaultValue:
              'A flow watches this pipeline and acts when a deal enters a stage. Create one to draw it.',
          })}
          muted
        />
      )}

      {flows.length > 0 && (
        <ul className="flex flex-col gap-2 border-t border-border pt-3">
          {flows.map((flow) => (
            <FlowCard
              key={flow.id}
              pipelineId={pipelineId}
              kind={kind}
              flow={flow}
              statusNames={statusNames}
              onChanged={revalidate}
            />
          ))}
        </ul>
      )}

      <NewFlowDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        pipelineId={pipelineId}
        onCreated={(flow) => {
          // Navigate from the flow the create returned rather than from a
          // re-read: the canvas is where the operator asked to go, and waiting
          // on the list first made a failed revalidation look like a failed
          // create.
          void navigate(`/pipelines/${pipelineId}/flows/${flow.id}`)
          revalidate()
        }}
      />
    </section>
  )
}
